<template>
  <div class="activity-chart">
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  Chart,
  Filler,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  type ChartConfiguration,
  type Plugin,
  type TooltipItem,
} from 'chart.js';
import { withAlpha } from '../../../utils/recordingActivity/colorScale';
import { withUnit } from '../../../utils/recordingActivity/format';
import type {
  ActivitySeries,
  ActivityTooltip,
  ChartPoint,
} from '../../../utils/recordingActivity/recordingActivity';

Chart.register(LineController, LineElement, PointElement, LinearScale, Filler, Tooltip);

const props = defineProps<{
  series: ActivitySeries;
  xUnit: string;
  /** In the backdrop's unit, so a flat walk's profile stays low. */
  backdropMinimumSpread: number;
  tooltip: (point: ChartPoint) => ActivityTooltip;
}>();

const canvas = ref<HTMLCanvasElement | null>(null);
let chart: Chart<'line', ChartPoint[]> | null = null;

const TICK_COLOR = 'rgba(255, 255, 255, 0.6)';
const GRID_COLOR = 'rgba(255, 255, 255, 0.1)';
const BACKDROP_COLOR = 'rgba(255, 255, 255, 0.07)';
const BASELINE_COLOR = 'rgba(255, 255, 255, 0.3)';

// Keyed to the y scale, so each reading takes its value's colour wherever it sits on the line; or,
// with stops, to the x scale, so each stretch takes its own.
function paint(target: Chart, colors: ActivitySeries['colors'], lowAlpha: number, highAlpha = lowAlpha): CanvasGradient | string {
  if ('color' in colors) return withAlpha(colors.color, (lowAlpha + highAlpha) / 2);
  const { chartArea, scales } = target;
  if ('stops' in colors) {
    if (!chartArea || !scales.x) return withAlpha(colors.stops[0]?.color ?? '#FFFFFF', highAlpha);
    const gradient = target.ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
    colors.stops.forEach(({ x, color }) => {
      const offset = (scales.x.getPixelForValue(x) - chartArea.left) / (chartArea.right - chartArea.left);
      gradient.addColorStop(Math.min(Math.max(offset, 0), 1), withAlpha(color, highAlpha));
    });
    return gradient;
  }
  const y = scales.y;
  if (!chartArea || !y) return withAlpha(colors.lowColor, lowAlpha);
  const gradient = target.ctx.createLinearGradient(0, y.getPixelForValue(colors.low), 0, y.getPixelForValue(colors.high));
  gradient.addColorStop(0, withAlpha(colors.lowColor, lowAlpha));
  gradient.addColorStop(1, withAlpha(colors.highColor, highAlpha));
  return gradient;
}

// Drawn under the line rather than as a dataset, so hovering only ever finds the reading.
function backdropPlugin(points: ChartPoint[] | null, minimumSpread: number): Plugin<'line'> {
  return {
    id: 'activityBackdrop',
    beforeDatasetsDraw(target) {
      if (!points || points.length < 2) return;
      const { ctx, chartArea, scales } = target;
      const lowest = Math.min(...points.map((p) => p.y));
      const spread = Math.max(Math.max(...points.map((p) => p.y)) - lowest, minimumSpread);
      const top = chartArea.top + chartArea.height * 0.1;
      const toY = (y: number) => chartArea.bottom - ((y - lowest) / spread) * (chartArea.bottom - top);

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(scales.x.getPixelForValue(points[0].x), chartArea.bottom);
      points.forEach((p) => ctx.lineTo(scales.x.getPixelForValue(p.x), toY(p.y)));
      ctx.lineTo(scales.x.getPixelForValue(points[points.length - 1].x), chartArea.bottom);
      ctx.closePath();
      ctx.fillStyle = BACKDROP_COLOR;
      ctx.fill();
      ctx.restore();
    },
  };
}

const hoverLinePlugin: Plugin<'line'> = {
  id: 'activityHoverLine',
  afterDatasetsDraw(target) {
    const active = target.tooltip?.getActiveElements?.() ?? [];
    if (active.length === 0) return;
    const { ctx, chartArea } = target;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(active[0].element.x, chartArea.top);
    ctx.lineTo(active[0].element.x, chartArea.bottom);
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
    ctx.stroke();
    ctx.restore();
  },
};

function config(series: ActivitySeries): ChartConfiguration<'line', ChartPoint[]> {
  return {
    type: 'line',
    data: {
      datasets: [
        {
          data: series.points,
          parsing: false,
          borderWidth: 2,
          borderColor: (ctx) => paint(ctx.chart, series.colors, 1),
          backgroundColor: (ctx) => paint(ctx.chart, series.colors, 0.08, 0.3),
          fill: series.baseline ? 'origin' : 'start',
          tension: 0.35,
          cubicInterpolationMode: 'monotone',
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderWidth: 0,
        },
      ],
    },
    plugins: [backdropPlugin(series.backdrop, props.backdropMinimumSpread), hoverLinePlugin],
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 250 },
      interaction: { mode: 'nearest', axis: 'x', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'rgba(40, 42, 50, 0.95)',
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: 8,
          displayColors: false,
          callbacks: {
            title: (items: TooltipItem<'line'>[]) => props.tooltip(items[0].raw as ChartPoint).title,
            label: (item: TooltipItem<'line'>) => props.tooltip(item.raw as ChartPoint).lines,
          },
        },
      },
      scales: {
        x: {
          type: 'linear',
          min: 0,
          max: series.xMax,
          afterBuildTicks: (scale) => {
            scale.ticks = series.xTicks.map((value) => ({ value }));
          },
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: TICK_COLOR,
            font: { size: 11 },
            callback: (value, index, ticks) => {
              const label = series.formatX(Number(value));
              return index === ticks.length - 1 ? `${label} ${props.xUnit}` : label;
            },
          },
        },
        y: {
          type: 'linear',
          reverse: series.reverse,
          min: series.yTicks ? series.yMin : undefined,
          max: series.yTicks ? series.yMax : undefined,
          suggestedMin: series.yMin,
          suggestedMax: series.yMax,
          afterBuildTicks: (scale) => {
            if (series.yTicks) scale.ticks = series.yTicks.map((value) => ({ value }));
          },
          grid: { color: (ctx) => (series.baseline && ctx.tick?.value === 0 ? BASELINE_COLOR : GRID_COLOR) },
          border: { display: false },
          ticks: {
            color: TICK_COLOR,
            font: { size: 11 },
            maxTicksLimit: 4,
            autoSkip: !series.yTicks,
            callback: (value, _index, ticks) => {
              const values = ticks.map((tick) => tick.value);
              const top = series.reverse ? Math.min(...values) : Math.max(...values);
              const label = series.formatValue(Number(value));
              return value === top ? withUnit(label, series.unit) : label;
            },
          },
        },
      },
    },
  };
}

function render() {
  chart?.destroy();
  chart = null;
  if (!canvas.value || props.series.points.length < 2) return;
  chart = new Chart(canvas.value, config(props.series));
}

onMounted(render);
watch(() => props.series, render);
onBeforeUnmount(() => chart?.destroy());
</script>

<style scoped>
.activity-chart {
  position: relative;
  height: 240px;
}

@media (max-width: 575.98px) {
  .activity-chart {
    height: 200px;
  }
}
</style>
